"use client"

import { useState, useCallback, useRef } from "react"
import NextImage from "next/image"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ImageIcon, Upload, Copy, Check, Download, Trash2, FileImage } from "lucide-react"
import { toast } from "sonner"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"

type OutputFormat = "data-uri" | "base64-only" | "css" | "html-img" | "markdown"

function formatOutput(dataUri: string, format: OutputFormat): string {
  const base64Only = dataUri.split(",")[1]

  switch (format) {
    case "data-uri":
      return dataUri
    case "base64-only":
      return base64Only
    case "css":
      return `background-image: url('${dataUri}');`
    case "html-img":
      return `<img src="${dataUri}" alt="Image" />`
    case "markdown":
      return `![Image](${dataUri})`
  }
}

export function Base64Image() {
  const [mode, setMode] = useState<"encode" | "decode">("encode")

  // Encode state
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [base64Output, setBase64Output] = useState("")
  const [outputFormat, setOutputFormat] = useState<OutputFormat>("data-uri")
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Decode state
  const [base64Input, setBase64Input] = useState("")
  const [decodedImage, setDecodedImage] = useState<string | null>(null)

  const { copy: copyToClipboard, isCopied } = useCopyToClipboard()

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file")
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB")
      return
    }

    setImageFile(file)

    const reader = new FileReader()
    reader.onload = (event) => {
      const dataUri = event.target?.result as string
      setImagePreview(dataUri)

      setBase64Output(formatOutput(dataUri, outputFormat))
      toast.success("Image encoded successfully")
    }
    reader.readAsDataURL(file)
  }, [outputFormat])

  // Re-generate when format changes
  const handleFormatChange = useCallback((format: OutputFormat) => {
    setOutputFormat(format)
    if (imagePreview) {
      setBase64Output(formatOutput(imagePreview, format))
    }
  }, [imagePreview])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file && fileInputRef.current) {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      fileInputRef.current.files = dataTransfer.files
      fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }, [])

  const decodeBase64 = useCallback(() => {
    if (!base64Input.trim()) {
      toast.error("Please enter Base64 data")
      return
    }

    try {
      let dataUri = base64Input.trim()

      // If it's just base64 without data URI prefix, try to detect and add it
      if (!dataUri.startsWith("data:")) {
        // Check if it looks like valid base64
        if (/^[A-Za-z0-9+/=]+$/.test(dataUri.replace(/\s/g, ""))) {
          // Try to detect image type from magic bytes
          const decoded = atob(dataUri.slice(0, 20))
          let mimeType = "image/png" // default

          if (decoded.startsWith("\xFF\xD8\xFF")) {
            mimeType = "image/jpeg"
          } else if (decoded.startsWith("\x89PNG")) {
            mimeType = "image/png"
          } else if (decoded.startsWith("GIF")) {
            mimeType = "image/gif"
          } else if (decoded.startsWith("RIFF") && decoded.includes("WEBP")) {
            mimeType = "image/webp"
          }

          dataUri = `data:${mimeType};base64,${dataUri.replace(/\s/g, "")}`
        } else {
          toast.error("Invalid Base64 data")
          return
        }
      }

      // Verify it's a valid image
      const img = document.createElement("img")
      img.onload = () => {
        setDecodedImage(dataUri)
        toast.success("Image decoded successfully")
      }
      img.onerror = () => {
        toast.error("Invalid image data")
        setDecodedImage(null)
      }
      img.src = dataUri
    } catch {
      toast.error("Failed to decode Base64")
      setDecodedImage(null)
    }
  }, [base64Input])

  const downloadDecodedImage = useCallback(() => {
    if (!decodedImage) return

    const link = document.createElement("a")
    link.href = decodedImage

    // Extract extension from mime type
    const mimeMatch = decodedImage.match(/data:(image\/\w+);/)
    const ext = mimeMatch ? mimeMatch[1].split("/")[1] : "png"

    link.download = `decoded-image.${ext}`
    link.click()
    toast.success("Image downloaded")
  }, [decodedImage])

  const handleCopy = useCallback(() => {
    if (!base64Output) return
    copyToClipboard(base64Output)
  }, [base64Output, copyToClipboard])

  const clear = useCallback(() => {
    setImageFile(null)
    setImagePreview(null)
    setBase64Output("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Base64 Image Converter
          </CardTitle>
          <CardDescription>
            Convert images to Base64 data URIs or decode Base64 back to images.
            All processing happens in your browser.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={(v) => setMode(v as "encode" | "decode")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="encode">Image → Base64</TabsTrigger>
              <TabsTrigger value="decode">Base64 → Image</TabsTrigger>
            </TabsList>

            {/* Encode Mode */}
            <TabsContent value="encode" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Output Format</Label>
                <Select value={outputFormat} onValueChange={(v) => handleFormatChange(v as OutputFormat)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="data-uri">Data URI (full)</SelectItem>
                    <SelectItem value="base64-only">Base64 Only</SelectItem>
                    <SelectItem value="css">CSS Background</SelectItem>
                    <SelectItem value="html-img">HTML &lt;img&gt; Tag</SelectItem>
                    <SelectItem value="markdown">Markdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                  imagePreview
                    ? "border-primary/50 bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50"
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <NextImage
                        src={imagePreview}
                        alt="Selected image preview"
                        width={200}
                        height={200}
                        unoptimized
                        className="max-w-[200px] max-h-[200px] rounded-lg object-contain shadow-lg"
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {imageFile?.name} ({(imageFile?.size || 0 / 1024).toFixed(1)} KB)
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        clear()
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="p-4 rounded-full bg-muted">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <p className="font-medium">Drop your image here</p>
                      <p className="text-sm text-muted-foreground">
                        or click to browse (PNG, JPG, GIF, WebP - max 5MB)
                      </p>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Output */}
              {base64Output && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Output ({base64Output.length.toLocaleString()} characters)</Label>
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      {isCopied() ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span className="ml-2">{isCopied() ? "Copied" : "Copy"}</span>
                    </Button>
                  </div>
                  <Textarea
                    value={base64Output}
                    readOnly
                    className="min-h-[150px] font-mono text-xs"
                  />
                </div>
              )}
            </TabsContent>

            {/* Decode Mode */}
            <TabsContent value="decode" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Base64 Input</Label>
                <Textarea
                  value={base64Input}
                  onChange={(e) => setBase64Input(e.target.value)}
                  placeholder="Paste Base64 or Data URI here..."
                  className="min-h-[150px] font-mono text-xs"
                />
              </div>

              <Button onClick={decodeBase64} className="w-full">
                <FileImage className="h-4 w-4 mr-2" />
                Decode Image
              </Button>

              {/* Decoded Image Preview */}
              {decodedImage && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Decoded Image</Label>
                    <Button variant="outline" size="sm" onClick={downloadDecodedImage}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4 flex justify-center bg-[url('/checkerboard.png')] bg-repeat">
                    <NextImage
                      src={decodedImage}
                      alt="Decoded image preview"
                      width={400}
                      height={400}
                      unoptimized
                      className="max-w-full max-h-[400px] object-contain"
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">About Base64 Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong>Data URIs</strong> allow embedding images directly in HTML, CSS, or code without external files.
          </p>
          <p>
            <strong>Pros:</strong> Reduces HTTP requests, works offline, good for small images/icons.
          </p>
          <p>
            <strong>Cons:</strong> Increases file size by ~33%, not cached separately, slower parsing.
          </p>
          <p>
            <strong>Best for:</strong> Small icons (&lt;10KB), single-use images, email templates.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
